import { useState, useContext, useMemo } from "react";
import styled, { ThemeProvider } from "styled-components/macro";
import { Form } from "../../../../../../layout/FormLayout";
import { useFormWrapper } from "../../../../../../util/util";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import Axios from "axios";
import type { StorefrontMetaData } from "../../../../../../types/types";
import { endpoints } from "../../../../../../endpoints";
import { Store } from "../../../../../../Store";
import { TwoColumnLayout } from "../../../../../../layout/portalPageLayout";
import {
  SoftHeader2,
  H3,
} from "../../../../../../components/Typography/Typography";
import {
  PrimaryButtonMedium,
  CancelButtonSmall,
} from "../../../../../../components/Buttons/Buttons";
import { makeNewTheme } from "../../../../../../theme/themeUtils";
import type {
  ThemeObjectSchema,
  DesignTokensSchema,
} from "../../../../../../theme/themeTypes";
import { useTranslation } from "react-i18next";
import { CardProduct } from "../../../../../../components/CardProduct/CardProduct";
import { ButtonTestPage } from "../../../../../TestPages/ButtonTestPage";
import {
  colorPrimitiveTokens,
  spacingTokens,
  radiusTokens,
  borderPrimitiveTokens,
  containerSettingsTokens,
  AllTokenKeys,
} from "../../../../../../designTokens";
import type { PIMProductMinimal } from "../../../../../../types/types.PIM";

const mockProduct: PIMProductMinimal = {
  id: "demo-product",
  name: "Chemical Product X",
  product_number: "CHEM-X-001",
  slug: "chemical-product-x",
  completion_score: 85,
  mandatory_completion_score: 100,
  display_completion_score: 85,
  display_mandatory_completion_score: 100,
  cover_image: null,
  status: "published",
  display_status: "published",
  last_modified_full: {
    modified_at: new Date().toISOString(),
    modified_by: "admin",
  },
  card_data: {
    brand_name: {
      display_name: "Brand Name",
      value: "ChemCorp",
    },
    cas_number: {
      display_name: "CAS Number",
      value: "7732-18-5",
    },
    chemical_name: {
      display_name: "Chemical Name",
      value: "Dihydrogen Monoxide",
    },
    product_description: {
      display_name: "Product Description",
      value:
        "A high-quality chemical compound for industrial applications. Perfect for demonstration purposes with extended description content.",
    },
    product_line: {
      display_name: "Product Line",
      value: "Industrial Solutions",
    },
  },
  show_total_completion_score: true,
  show_mandatory_completion_score: true,
  sds_assets: [
    {
      cdn_url: null,
      file_name: "demo-sds.pdf",
      id: "sds-asset-1",
      name: "Demo SDS",
      number: "SDS-001",
      preview_url: "/preview/sds-1",
      signed_url: "https://example.com/sds-document.pdf",
      size: 195442,
    },
  ],
  tds_assets: [
    {
      cdn_url: null,
      file_name: "demo-tds.pdf",
      id: "tds-asset-1",
      name: "Demo TDS",
      number: "TDS-001",
      preview_url: "/preview/tds-1",
      signed_url: "https://example.com/tds-document.pdf",
      size: 129079,
    },
  ],
  has_multiple_documents: true,
  has_sample_sku: true,
  pim_product_type: "master",
};

const ConfigWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  height: calc(100vh - 200px);
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.resolve("spacing_sm")};

  @media (max-width: 768px) {
    overflow-y: visible;
    padding-right: 0;
  }
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  overflow-y: auto;

  @media (max-width: 768px) {
    overflow-y: visible;
  }
`;

const PreviewContainer = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  max-width: 600px;
`;

const ResponsivePreviewWrapper = styled.div<{ viewportWidth: string }>`
  width: ${({ viewportWidth }) => viewportWidth};
  max-width: 100%;
  margin: 0 auto;
  transition: width 0.3s ease;
  border: 1px solid ${({ theme }) => theme.resolve("color_border_light")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  overflow: hidden;
  background-color: #fff;
`;

const ViewportSelector = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  flex-wrap: wrap;
`;

const ViewportButton = styled.button<{ isActive: boolean }>`
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_sm")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ isActive, theme }) =>
    isActive
      ? theme.resolve("color_bg_surface_brand")
      : theme.resolve("color_bg_surface_standard")};
  color: ${({ isActive, theme }) =>
    isActive
      ? theme.resolve("color_text_inverse")
      : theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive
        ? theme.resolve("color_bg_surface_brand")
        : theme.resolve("color_bg_surface_subtle")};
  }
`;

const ConfigGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  padding: ${({ theme }) => theme.resolve("spacing_md")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const ConfigGroupTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const TokenSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_sm")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }
`;

const ColorPicker = styled.input`
  width: 100%;
  height: 40px;
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_sm")};
  border: 1px solid ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }

  /* Remove spinner for webkit browsers */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const OpacitySlider = styled.input`
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  outline: none;
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_brand")};
    border: 2px solid ${({ theme }) => theme.resolve("color_border_brand")};
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_brand")};
    border: 2px solid ${({ theme }) => theme.resolve("color_border_brand")};
    border-radius: 50%;
    cursor: pointer;
  }
`;

const ColorInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ColorWithOpacity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const OpacityValue = styled.span`
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  min-width: 35px;
`;

interface TokenConfiguratorProps {
  onClose: () => void;
  mutateStorefrontMetadata: (
    data: StorefrontMetaData,
    shouldRevalidate: boolean
  ) => void;
}

// Define configurable tokens structure as a function to support dynamic options
const getConfigurableTokens = (colorOptions: Record<string, string>) => ({
  cardProduct: {
    // Background
    background: {
      tokenName: "color_cardProduct_bg",
      label: "Card Background",
      type: "color",
      isCustomColor: true,
      options: colorOptions,
    },
    // Border Colors
    border: {
      tokenName: "color_cardProduct_border",
      label: "Card Border Color",
      type: "select",
      options: colorOptions,
    },
    borderHover: {
      tokenName: "color_cardProduct_border_hover",
      label: "Card Border Hover Color",
      type: "select",
      options: colorOptions,
    },
    // Border Widths - Default State
    borderTop: {
      tokenName: "border_cardProduct_top",
      label: "Border Width - Top",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderRight: {
      tokenName: "border_cardProduct_right",
      label: "Border Width - Right",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderBottom: {
      tokenName: "border_cardProduct_bottom",
      label: "Border Width - Bottom",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderLeft: {
      tokenName: "border_cardProduct_left",
      label: "Border Width - Left",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Border Widths - Hover State
    borderTopHover: {
      tokenName: "border_cardProduct_top_hover",
      label: "Border Width - Top (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderRightHover: {
      tokenName: "border_cardProduct_right_hover",
      label: "Border Width - Right (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderBottomHover: {
      tokenName: "border_cardProduct_bottom_hover",
      label: "Border Width - Bottom (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderLeftHover: {
      tokenName: "border_cardProduct_left_hover",
      label: "Border Width - Left (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Radii
    borderRadius: {
      tokenName: "radius_cardProduct",
      label: "Card Border Radius",
      type: "select",
      options: radiusTokens,
    },
    imageBorderRadius: {
      tokenName: "radius_cardProduct_image",
      label: "Image Border Radius",
      type: "select",
      options: radiusTokens,
    },
    // Spacing
    spacing: {
      tokenName: "spacing_cardProduct_gap",
      label: "Card Internal Spacing",
      type: "select",
      options: spacingTokens,
    },
    imagePadding: {
      tokenName: "spacing_cardProduct_image_padding",
      label: "Image Padding",
      type: "select",
      options: spacingTokens,
    },
    // Shadow
    shadowX: {
      tokenName: "effect_cardProduct_shadow_x",
      label: "Shadow X Offset",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values
    },
    shadowY: {
      tokenName: "effect_cardProduct_shadow_y",
      label: "Shadow Y Offset",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values
    },
    shadowBlur: {
      tokenName: "effect_cardProduct_shadow_blur",
      label: "Shadow Blur",
      type: "number",
      unit: "px",
      min: 0, // Blur should be non-negative
    },
    shadowSpread: {
      tokenName: "effect_cardProduct_shadow_spread",
      label: "Shadow Spread",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values for inset shadows
    },
    shadowColor: {
      tokenName: "effect_cardProduct_shadow_color",
      label: "Shadow Color",
      type: "colorWithOpacity",
      isCustomColor: true,
    },
    // Text Colors
    textProductName: {
      tokenName: "color_cardProduct_text_product_name",
      label: "Product Name Color",
      type: "select",
      options: colorOptions,
    },
    textAttributeName: {
      tokenName: "color_cardProduct_text_attribute_name",
      label: "Attribute Name Color",
      type: "select",
      options: colorOptions,
    },
    textAttributeValue: {
      tokenName: "color_cardProduct_text_attribute_value",
      label: "Attribute Value Color",
      type: "select",
      options: colorOptions,
    },
    // Icon Color
    iconColor: {
      tokenName: "color_cardProduct_icon",
      label: "Icon Color",
      type: "select",
      options: colorOptions,
    },
    // Aspect Ratio
    aspectRatio: {
      tokenName: "aspect_cardProduct_default",
      label: "Card Aspect Ratio",
      type: "select",
      options: {
        "3 / 2": "3:2 Landscape",
        "1 / 1": "1:1 Square",
        "16 / 9": "16:9 Widescreen",
      },
      description: "Choose the aspect ratio for card products",
    },
  },
  filterPanel: {
    // Backgrounds
    searchBarBackground: {
      tokenName: "color_filterSearchBar_bg",
      label: "Filter Search Bar Background",
      type: "color",
      isCustomColor: true,
    },
    filterGroupBackgroundCollpased: {
      tokenName: "color_filterGroup_bg_collpased",
      label: "Filter Group Background (Collapsed)",
      type: "color",
      isCustomColor: true,
    },
    filterGroupBackgroundExpanded: {
      tokenName: "color_filterGroup_bg_expanded",
      label: "Filter Group Background (Expanded)",
      type: "color",
      isCustomColor: true,
    },
    // Borders
    searchBarBorder: {
      tokenName: "color_filterSearchBar_border",
      label: "Filter Search Bar Border Color",
      type: "select",
      options: colorOptions,
    },
    filterGroupBorderCollpased: {
      tokenName: "color_filterGroup_border_collpased",
      label: "Filter Group Border (Collapsed)",
      type: "select",
      options: colorOptions,
    },
    filterGroupBorderExpanded: {
      tokenName: "color_filterGroup_border_expanded",
      label: "Filter Group Border (Expanded)",
      type: "select",
      options: colorOptions,
    },
    filterPanelBorder: {
      tokenName: "color_filterPanel_border",
      label: "Filter Panel Border Color",
      type: "select",
      options: colorOptions,
    },
    // border width
    filterGroupBorderWidth: {
      tokenName: "border_filterGroup",
      label: "Filter Group Border Width",
      type: "select",
      options: borderPrimitiveTokens,
    },
    filterPanelBorderWidth: {
      tokenName: "border_filterPanel",
      label: "Filter Panel Border Width",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Spacing
    filterItemGap: {
      tokenName: "spacing_filterItem_gap",
      label: "Filter Item Gap",
      type: "select",
      options: spacingTokens,
    },
    filterPanelSpacing: {
      tokenName: "spacing_filterPanel",
      label: "Filter Panel Spacing",
      type: "select",
      options: spacingTokens,
    },
  },
  productDetail: {
    productBackground: {
      tokenName: "color_bg_surface_product",
      label: "Product Background",
      type: "color",
      isCustomColor: true,
    },
    containerSettings: {
      tokenName: "container_settings",
      label: "Container Settings",
      type: "select",
      options: containerSettingsTokens,
    },
    // tabListSpacing: {
    //   tokenName: "spacing_productDetail_tabList",
    //   label: "Tab List Spacing",
    //   type: "select",
    //   options: spacingTokens,
    // },
  },
  buttons: {
    // Shared Button Properties
    borderRadius: {
      tokenName: "radius_button_standard",
      label: "Button Border Radius",
      type: "select",
      options: radiusTokens,
      description: "Border radius for all buttons",
    },
    // Primary Button
    primaryBgEnabled: {
      tokenName: "color_button_bg_primary_enabled",
      label: "Primary Background (Default)",
      type: "select",
      options: colorOptions,
    },
    primaryBgHover: {
      tokenName: "color_button_bg_primary_hover",
      label: "Primary Background (Hover)",
      type: "select",
      options: colorOptions,
    },
    primaryTextEnabled: {
      tokenName: "color_button_text_primary_enabled",
      label: "Primary Text Color",
      type: "select",
      options: colorOptions,
    },
    primaryTextHover: {
      tokenName: "color_button_text_primary_hover",
      label: "Primary Text Color (Hover)",
      type: "select",
      options: colorOptions,
    },
    primaryBorderEnabled: {
      tokenName: "color_button_border_primary_enabled",
      label: "Primary Border Color",
      type: "select",
      options: colorOptions,
    },
    primaryIconEnabled: {
      tokenName: "color_button_icon_primary_enabled",
      label: "Primary Icon Color",
      type: "select",
      options: colorOptions,
    },
  },
  customColors: {
    custom1: {
      tokenName: "color_custom_1",
      label: "Custom Color 1",
      type: "color",
      isCustomColor: true,
    },
    custom2: {
      tokenName: "color_custom_2",
      label: "Custom Color 2",
      type: "color",
      isCustomColor: true,
    },
    custom3: {
      tokenName: "color_custom_3",
      label: "Custom Color 3",
      type: "color",
      isCustomColor: true,
    },
    custom4: {
      tokenName: "color_custom_4",
      label: "Custom Color 4",
      type: "color",
      isCustomColor: true,
    },
  },
  customBorders: {
    custom1: {
      tokenName: "border_custom_1",
      label: "Custom Border 1",
      type: "number",
      unit: "px",
      defaultValue: "6",
      min: 0, // Border widths should be non-negative
    },
    custom2: {
      tokenName: "border_custom_2",
      label: "Custom Border 2",
      type: "number",
      unit: "px",
      defaultValue: "8",
      min: 0, // Border widths should be non-negative
    },
  },
});

export const AdvancedThemeEditor = ({
  onClose,
  mutateStorefrontMetadata,
}: TokenConfiguratorProps) => {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storeState, storeDispatch } = useContext(Store);
  const { t } = useTranslation();
  const { storefront_id, storefront_metadata } = storeState;
  const branding = storefront_metadata.theme_object.branding;

  const [submitting, setSubmitting] = useState(false);
  const [selectedViewport, setSelectedViewport] = useState("desktop");

  // Initialize form state from saved designTokens
  const initializeFormState = () => {
    const initialTokenOverrides: Record<string, string> = {};
    const initialCustomValues: Record<string, any> = {};

    // Check if we have saved designTokens in the new schema
    const savedThemeObject =
      storefront_metadata.theme_object as ThemeObjectSchema;
    if (savedThemeObject?.designTokens) {
      const { designTokens } = savedThemeObject;

      // Load CardProduct, filterPanel and Button token overrides
      [
        designTokens.cardProduct,
        designTokens.buttons,
        designTokens.filterPanel,
        designTokens.productDetail,
      ].forEach((category) => {
        if (category) {
          Object.entries(category).forEach(([tokenName, tokenValue]) => {
            if (typeof tokenValue === "object" && tokenValue.type === "token") {
              initialTokenOverrides[tokenName] = tokenValue.value;
            } else if (
              typeof tokenValue === "object" &&
              tokenValue.type === "custom"
            ) {
              initialCustomValues[tokenName] = tokenValue.value;

              // Parse rgba values for color with opacity
              if (
                tokenName === "effect_cardProduct_shadow_color" &&
                typeof tokenValue.value === "string" &&
                tokenValue.value.startsWith("rgba(")
              ) {
                const match = tokenValue.value.match(
                  /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/
                );
                if (match) {
                  const [, r, g, b, a] = match;
                  const hex =
                    "#" +
                    (
                      (1 << 24) +
                      (parseInt(r) << 16) +
                      (parseInt(g) << 8) +
                      parseInt(b)
                    )
                      .toString(16)
                      .slice(1);
                  initialCustomValues[`${tokenName}_color`] = hex;
                  initialCustomValues[`${tokenName}_opacity`] = parseFloat(a);
                }
              }
            }
          });
        }
      });

      // Load custom colors
      if (designTokens.customColors) {
        Object.entries(designTokens.customColors).forEach(
          ([tokenName, value]) => {
            initialCustomValues[tokenName] = value;
          }
        );
      }

      // Load custom borders (remove px suffix for form input)
      if (designTokens.customBorders) {
        Object.entries(designTokens.customBorders).forEach(
          ([tokenName, value]) => {
            const numericValue =
              typeof value === "string" ? value.replace("px", "") : value;
            initialCustomValues[tokenName] = numericValue;
          }
        );
      }
    }

    return { initialTokenOverrides, initialCustomValues };
  };

  const { initialTokenOverrides, initialCustomValues } = initializeFormState();
  const [tokenOverrides, setTokenOverrides] = useState<Record<string, string>>(
    initialTokenOverrides
  );
  const [customValues, setCustomValues] =
    useState<Record<string, any>>(initialCustomValues);

  // Build color options including custom colors from current form state
  const getColorOptions = (): typeof colorPrimitiveTokens &
    Record<string, string> => {
    const baseOptions = { ...colorPrimitiveTokens };

    // Add custom colors from current form state
    Object.entries(customValues).forEach(([tokenName, value]) => {
      if (tokenName.startsWith("color_custom_") && value) {
        (baseOptions as Record<string, string>)[
          tokenName
        ] = `Custom (${value})`;
      }
    });

    return baseOptions;
  };

  const configurableTokens = getConfigurableTokens(getColorOptions());

  const viewportSizes = {
    mobile: "375px",
    desktop: "100%",
  };

  // Create preview theme with current token overrides
  const previewTheme = useMemo(() => {
    console.info("🎨 Creating preview theme with overrides:", tokenOverrides);
    console.info("🎨 Creating preview theme with custom values:", customValues);
    const baseTheme = makeNewTheme(branding, storeState.tenant_id);

    if (
      Object.keys(tokenOverrides).length === 0 &&
      Object.keys(customValues).length === 0
    ) {
      console.info("📋 No token overrides or custom values, using base theme");
      return baseTheme;
    }

    // Create a new theme with direct token value overrides
    const themeWithOverrides = {
      ...baseTheme,
      tokens: {
        ...baseTheme.tokens,
      },
      customValues, // Add custom values to theme
    };

    console.info(
      "🔍 Available primitive tokens:",
      Object.keys(themeWithOverrides.tokens.primitiveTokens)
    );

    // Override the resolve function to use our token overrides
    const originalResolve = themeWithOverrides.resolve;
    themeWithOverrides.resolve = (tokenName: AllTokenKeys) => {
      const originalValue = originalResolve(tokenName);

      // Check for custom values first
      if (customValues[tokenName] !== undefined) {
        const customValue = customValues[tokenName];
        console.info(`🎨 Custom value applied: ${tokenName} = ${customValue}`);

        // For numbers with units, append the unit
        if (
          typeof customValue === "number" ||
          (typeof customValue === "string" && /^\d+$/.test(customValue))
        ) {
          // Check if this is a shadow or border token that needs px
          if (
            tokenName.includes("shadow") ||
            tokenName.includes("border_custom")
          ) {
            return `${customValue}px`;
          }
          // Aspect ratio tokens are now handled as direct CSS values (e.g., "3 / 2")
          // No special processing needed for aspect ratios
        }

        return customValue;
      }

      // If this token has an override, use the primitive token value directly
      if (tokenOverrides[tokenName]) {
        const primitiveTokenName = tokenOverrides[tokenName];
        console.info(
          `🔄 Token override: ${tokenName} -> ${primitiveTokenName}`
        );

        // For aspect ratio default selection, primitiveTokenName is the CSS ratio value
        if (tokenName === "aspect_cardProduct_default") {
          console.info(
            `✅ Aspect ratio override applied: ${tokenName} = ${primitiveTokenName}`
          );
          return primitiveTokenName;
        }

        // Get the primitive token value - need to access it properly
        const allPrimitiveTokens = {
          ...themeWithOverrides.tokens.primitiveTokens.colors,
          ...themeWithOverrides.tokens.primitiveTokens.spacing,
          ...themeWithOverrides.tokens.primitiveTokens.radii,
          ...themeWithOverrides.tokens.primitiveTokens.fontSizes,
          ...themeWithOverrides.tokens.primitiveTokens.fontWeights,
          ...themeWithOverrides.tokens.primitiveTokens.borders,
        };

        console.info(
          `🔍 Looking for primitive token: ${primitiveTokenName} in:`,
          Object.keys(allPrimitiveTokens)
        );

        const primitiveValue =
          allPrimitiveTokens[
            primitiveTokenName as keyof typeof allPrimitiveTokens
          ];

        if (primitiveValue) {
          console.info(
            `✅ Override applied: ${tokenName} = ${primitiveValue} (was ${originalValue})`
          );
          return primitiveValue;
        } else {
          console.info(
            `❌ Primitive token not found: ${primitiveTokenName}, falling back to original`
          );
        }
      }

      return originalValue;
    };

    return themeWithOverrides;
  }, [branding, tokenOverrides, customValues, storeState.tenant_id]);

  const { handleSubmit } = useFormWrapper({
    defaultValues: {},
  });

  const handleTokenChange = (tokenName: string, primitiveTokenName: string) => {
    console.info(`🎯 Token change: ${tokenName} -> ${primitiveTokenName}`);
    setTokenOverrides((prev) => {
      const newOverrides = {
        ...prev,
        [tokenName]: primitiveTokenName,
      };
      console.info("🗂️ All token overrides:", newOverrides);
      return newOverrides;
    });
  };

  const handleCustomValueChange = (tokenName: string, value: any) => {
    console.info(`🎨 Custom value change: ${tokenName} -> ${value}`);
    setCustomValues((prev) => {
      const newValues = {
        ...prev,
        [tokenName]: value,
      };
      console.info("🗂️ All custom values:", newValues);
      return newValues;
    });
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      // Build designTokens structure from form state
      const designTokens: DesignTokensSchema = {};

      // Process token overrides
      Object.entries(tokenOverrides).forEach(([tokenName, primitiveToken]) => {
        // Find which category this token belongs to
        const category = Object.entries(configurableTokens).find(
          ([_, config]) =>
            Object.values(config).some(
              (item: any) => item.tokenName === tokenName
            )
        )?.[0];

        if (category === "cardProduct") {
          if (!designTokens.cardProduct) designTokens.cardProduct = {};
          (designTokens.cardProduct as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "filterPanel") {
          if (!designTokens.filterPanel) designTokens.filterPanel = {};
          (designTokens.filterPanel as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "productDetail") {
          if (!designTokens.productDetail) designTokens.productDetail = {};
          (designTokens.productDetail as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "buttons") {
          if (!designTokens.buttons) designTokens.buttons = {};
          (designTokens.buttons as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        }
      });

      // Process custom values
      Object.entries(customValues).forEach(([tokenName, value]) => {
        // Custom colors
        if (tokenName.startsWith("color_custom_")) {
          if (!designTokens.customColors) designTokens.customColors = {};
          designTokens.customColors[tokenName] = value;
        }
        // Custom borders
        else if (tokenName.startsWith("border_custom_")) {
          if (!designTokens.customBorders) designTokens.customBorders = {};
          designTokens.customBorders[tokenName] = `${value}px`;
        }
        // Other custom values (like shadow offsets)
        else {
          const category = Object.entries(configurableTokens).find(
            ([_, config]) =>
              Object.values(config).some(
                (item: any) => item.tokenName === tokenName
              )
          )?.[0];

          if (category === "cardProduct") {
            if (!designTokens.cardProduct) designTokens.cardProduct = {};
            (designTokens.cardProduct as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          } else if (category === "filterPanel") {
            if (!designTokens.filterPanel) designTokens.filterPanel = {};
            (designTokens.filterPanel as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          } else if (category === "productDetail") {
            if (!designTokens.productDetail) designTokens.productDetail = {};
            (designTokens.productDetail as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          }
        }
      });

      // Create new ThemeObjectSchema structure
      const themeObject: ThemeObjectSchema = {
        version: "1.0",
        branding: {
          ...(branding.brandColor && { brandColor: branding.brandColor }),
          ...(branding.portalLogoUrl && {
            portalLogoUrl: branding.portalLogoUrl,
          }),
          ...(branding.publicLogoUrl && {
            publicLogoUrl: branding.publicLogoUrl,
          }),
          ...(branding.customFontFamily && {
            customFontFamily: branding.customFontFamily,
          }),
          ...(branding.favIconUrl && { favIconUrl: branding.favIconUrl }),
        },
        // Only include designTokens if there are any overrides
        ...(Object.keys(designTokens).length > 0 ? { designTokens } : {}),
      };

      const patchBody: Pick<StorefrontMetaData, "theme_object"> = {
        theme_object: themeObject as any,
      };

      const { data: returnedMetadata } = await Axios.patch<StorefrontMetaData>(
        endpoints.v1_storefronts_id_metadata(storefront_id),
        patchBody
      );

      storeDispatch({
        type: "SET_STOREFRONT_METADATA",
        payload: returnedMetadata,
      });

      storeDispatch({
        type: "SET_THEME",
        payload: makeNewTheme(themeObject, storeState.tenant_id),
      });

      notifySuccess(t("Theme changes saved"));
      setSubmitting(false);
      mutateStorefrontMetadata(returnedMetadata, false);
      onClose();
    } catch (error) {
      notifyError(t("There was an error saving the theme changes"), { error });
      setSubmitting(false);
    }
  };

  const renderTokenConfigurator = (
    sectionKey: string,
    config: Record<string, any>
  ) => {
    const getSectionTitle = (key: string) => {
      switch (key) {
        case "cardProduct":
          return "Product Cards";
        case "filterPanel":
          return "Filter Panel";
        case "productDetail":
          return "Product Detail";
        case "buttons":
          return "Buttons";
        case "customColors":
          return "Custom Colors";
        case "customBorders":
          return "Custom Borders";
        default:
          return key;
      }
    };

    return (
      <ConfigGroup key={sectionKey}>
        <ConfigGroupTitle>{getSectionTitle(sectionKey)}</ConfigGroupTitle>
        {Object.entries(config).map(([configKey, tokenConfig]) => (
          <div key={configKey}>
            <SoftHeader2>{tokenConfig.label}</SoftHeader2>
            {tokenConfig.description && (
              <p
                style={{ fontSize: "12px", color: "#6F777F", margin: "4px 0" }}
              >
                {tokenConfig.description}
              </p>
            )}

            {/* Color picker for custom colors */}
            {tokenConfig.type === "color" && tokenConfig.isCustomColor && (
              <ColorPicker
                type="color"
                value={
                  customValues[tokenConfig.tokenName] ||
                  (tokenConfig.tokenName === "color_custom_1"
                    ? "#3968FF"
                    : tokenConfig.tokenName === "color_custom_2"
                    ? "#9E77ED"
                    : tokenConfig.tokenName === "color_custom_3"
                    ? "#66C61C"
                    : tokenConfig.tokenName === "color_custom_4"
                    ? "#EE46BC"
                    : "#3968FF")
                }
                onChange={(e) => {
                  handleCustomValueChange(
                    tokenConfig.tokenName,
                    e.target.value
                  );
                }}
              />
            )}

            {/* Color picker with opacity for shadow colors */}
            {tokenConfig.type === "colorWithOpacity" &&
              tokenConfig.isCustomColor && (
                <ColorInputWrapper>
                  <ColorWithOpacity>
                    <ColorPicker
                      type="color"
                      value={
                        customValues[`${tokenConfig.tokenName}_color`] ||
                        "#000000"
                      }
                      onChange={(e) => {
                        const opacity =
                          customValues[`${tokenConfig.tokenName}_opacity`] ||
                          0.1;
                        handleCustomValueChange(
                          `${tokenConfig.tokenName}_color`,
                          e.target.value
                        );
                        // Update the combined value with opacity
                        const rgb = parseInt(e.target.value.slice(1), 16);
                        const r = (rgb >> 16) & 255;
                        const g = (rgb >> 8) & 255;
                        const b = rgb & 255;
                        handleCustomValueChange(
                          tokenConfig.tokenName,
                          `rgba(${r}, ${g}, ${b}, ${opacity})`
                        );
                      }}
                    />
                    <OpacityValue>
                      {Math.round(
                        (customValues[`${tokenConfig.tokenName}_opacity`] ||
                          0.1) * 100
                      )}
                      %
                    </OpacityValue>
                  </ColorWithOpacity>
                  <OpacitySlider
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={
                      customValues[`${tokenConfig.tokenName}_opacity`] || 0.1
                    }
                    onChange={(e) => {
                      const opacity = parseFloat(e.target.value);
                      handleCustomValueChange(
                        `${tokenConfig.tokenName}_opacity`,
                        opacity
                      );
                      // Update the combined value with new opacity
                      const color =
                        customValues[`${tokenConfig.tokenName}_color`] ||
                        "#000000";
                      const rgb = parseInt(color.slice(1), 16);
                      const r = (rgb >> 16) & 255;
                      const g = (rgb >> 8) & 255;
                      const b = rgb & 255;
                      handleCustomValueChange(
                        tokenConfig.tokenName,
                        `rgba(${r}, ${g}, ${b}, ${opacity})`
                      );
                    }}
                  />
                </ColorInputWrapper>
              )}

            {/* Number input for custom values */}
            {tokenConfig.type === "number" && (
              <NumberInput
                type="number"
                min={tokenConfig.allowNegative ? undefined : tokenConfig.min}
                value={
                  customValues[tokenConfig.tokenName] ||
                  tokenConfig.defaultValue ||
                  "0"
                }
                onChange={(e) => {
                  handleCustomValueChange(
                    tokenConfig.tokenName,
                    e.target.value
                  );
                }}
                placeholder={`Enter value${
                  tokenConfig.unit ? ` in ${tokenConfig.unit}` : ""
                }`}
              />
            )}

            {/* Boolean checkbox */}
            {tokenConfig.type === "boolean" && (
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  checked={customValues[tokenConfig.tokenName] || false}
                  onChange={(e) => {
                    handleCustomValueChange(
                      tokenConfig.tokenName,
                      e.target.checked
                    );
                  }}
                />
                <span>{tokenConfig.label}</span>
              </CheckboxWrapper>
            )}

            {/* Select dropdown for token options */}
            {tokenConfig.type === "select" && tokenConfig.options && (
              <TokenSelect
                value={tokenOverrides[tokenConfig.tokenName] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleTokenChange(tokenConfig.tokenName, value);
                  } else {
                    setTokenOverrides((prev) => {
                      const newOverrides = { ...prev };
                      delete newOverrides[tokenConfig.tokenName];
                      return newOverrides;
                    });
                  }
                }}
              >
                <option value="">{t("Use default value")}</option>
                {Object.entries(tokenConfig.options).map(([key, value]) => (
                  <option key={key} value={key}>
                    {key} ({value})
                  </option>
                ))}
              </TokenSelect>
            )}
          </div>
        ))}
      </ConfigGroup>
    );
  };

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <ConfigWrapper>
        <ConfigSection>
          <H3>{t("Token Configuration")}</H3>
          {Object.entries(configurableTokens).map(([sectionKey, config]) =>
            renderTokenConfigurator(sectionKey, config)
          )}
        </ConfigSection>

        <PreviewSection>
          <H3>{t("Live Preview")}</H3>
          <PreviewContainer>
            <SoftHeader2>{t("Product Card Preview")}</SoftHeader2>

            <ViewportSelector>
              {Object.entries(viewportSizes).map(([viewport, width]) => (
                <ViewportButton
                  key={viewport}
                  isActive={selectedViewport === viewport}
                  onClick={() => setSelectedViewport(viewport)}
                  type="button"
                >
                  {viewport.charAt(0).toUpperCase() + viewport.slice(1)} (
                  {width})
                </ViewportButton>
              ))}
            </ViewportSelector>

            <ResponsivePreviewWrapper
              viewportWidth={
                viewportSizes[selectedViewport as keyof typeof viewportSizes]
              }
            >
              <ThemeProvider theme={previewTheme}>
                <PreviewGrid>
                  <CardProduct
                    product={mockProduct as any}
                    onProductClick={() => {}}
                    onQuoteClick={() => {}}
                    onSampleClick={() => {}}
                    onDocumentClick={() => {}}
                  />
                  <CardProduct
                    product={mockProduct as any}
                    onProductClick={() => {}}
                    onQuoteClick={() => {}}
                    onSampleClick={() => {}}
                    onDocumentClick={() => {}}
                  />
                  <CardProduct
                    product={mockProduct as any}
                    onProductClick={() => {}}
                    onQuoteClick={() => {}}
                    onSampleClick={() => {}}
                    onDocumentClick={() => {}}
                  />
                  <CardProduct
                    product={mockProduct as any}
                    onProductClick={() => {}}
                    onQuoteClick={() => {}}
                    onSampleClick={() => {}}
                    onDocumentClick={() => {}}
                  />
                </PreviewGrid>
              </ThemeProvider>
            </ResponsivePreviewWrapper>
          </PreviewContainer>

          <PreviewContainer>
            <SoftHeader2>{t("Button Preview")}</SoftHeader2>
            <ThemeProvider theme={previewTheme}>
              <ButtonTestPage />
            </ThemeProvider>
          </PreviewContainer>
        </PreviewSection>
      </ConfigWrapper>

      <TwoColumnLayout>
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <PrimaryButtonMedium loading={submitting}>
            {t("Save Theme Changes")}
          </PrimaryButtonMedium>
          <CancelButtonSmall onClick={onClose}>{t("Cancel")}</CancelButtonSmall>
        </div>
      </TwoColumnLayout>
    </Form>
  );
};
